import { BREAKPOINTS } from "../../config/breakpoints";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { errorNotification } from "../../Generic/Notifications";
import { Table, Tbody, Tcol, Thead, Trow } from "../../Generic/Table";
import EmptyState from "../../Generic/EmptyState";
import Loader from "../../Generic/Loader";
import Button from "../../Generic/Button";
import Modal from "../../Generic/Modal";
import AuditLogFilters from "./AuditLogFilters";
import AuditLogService from "../Services/AuditLogService";
import { AuditAction, AUDIT_LOG_PAGE_SIZE } from "../constants/auditLog.constants";
import { beautifyKey, isEmpty } from "../../util/util";
import { formatDisplayDate } from "../../util/date.util";

const DATA_MODES = {
	APPEND: "APPEND",
	REPLACE: "REPLACE",
};

const Page = styled.div`
	display: flex;
	height: max-content;
	flex-direction: column;
	text-align: left;
	width: 100%;
	padding: 2rem;
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		padding: 1rem;
	}
`;

const Header = styled.div`
	display: flex;
	font-size: 2rem;
	margin: 0 0 1rem 0.5rem;
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		font-size: 1.5rem;
	}
`;

const CenteredContainer = styled.div`
	width: 100%;
	height: max-content;
	display: flex;
	flex-direction: column;
	align-items: center;
	margin: ${(props) => props.margin || "1rem 0"};
`;

function buildGetAuditLogsBody(page, filters) {
	const body = {
		page,
		limit: AUDIT_LOG_PAGE_SIZE,
	};

	if (filters.action && Object.values(AuditAction).includes(filters.action)) {
		body.action = filters.action;
	}

	const email = filters.actorEmail?.trim();
	const uid = filters.actorUid?.trim();
	if (email) {
		body.actorEmail = email;
	}
	if (uid) {
		body.actorUid = uid;
	}

	if (filters.targets?.length > 0) {
		body.targets = filters.targets;
	}

	if (filters.startDate) {
		body.startDate = filters.startDate;
	}
	if (filters.endDate) {
		body.endDate = filters.endDate;
	}

	return body;
}

const AuditLogs = () => {
	const [appliedFilters, setAppliedFilters] = useState({});
	const [items, setItems] = useState([]);
	const [page, setPage] = useState(1);
	const [mode, setMode] = useState(DATA_MODES.REPLACE);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [prefetchSentinel, setPrefetchSentinel] = useState(null);
	const [logDetails, setLogDetails] = useState(null);

	const hasMore = useRef(true);
	const requestId = useRef(0);
	const loadingRef = useRef(false);
	const appliedFiltersRef = useRef(appliedFilters);

	useEffect(() => {
		appliedFiltersRef.current = appliedFilters;
	}, [appliedFilters]);

	const fetchLogs = useCallback(async (pageNum, fetchMode, filters) => {
		const id = ++requestId.current;
		loadingRef.current = true;
		setLoading(true);

		const body = buildGetAuditLogsBody(pageNum, filters);
		const { data, error: fetchError } = await AuditLogService.getAuditLogs(body);

		// A newer Apply/Clear/scroll fetch has started; applying this result would mix pages or filters.
		if (id !== requestId.current) {
			return;
		}

		if (fetchError) {
			errorNotification(fetchError);
			if (fetchMode === DATA_MODES.REPLACE) {
				setError(true);
			}
			loadingRef.current = false;
			setLoading(false);
			return;
		}

		setError(false);
		const pageItems = data?.items ?? [];
		const total = data?.total ?? 0;

		if (fetchMode === DATA_MODES.REPLACE) {
			setItems(pageItems);
			hasMore.current = pageItems.length < total;
		} else {
			setItems((prev) => {
				const nextItems = [...prev, ...pageItems];
				hasMore.current = nextItems.length < total;
				return nextItems;
			});
		}

		loadingRef.current = false;
		setLoading(false);
	}, []);

	const observer = useRef(
		new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && hasMore.current && !loadingRef.current) {
					setMode(DATA_MODES.APPEND);
					setPage((currentPage) => currentPage + 1);
				}
			},
			{ threshold: 0.6 }
		)
	);

	useEffect(() => {
		if (items.length < 3) {
			setPrefetchSentinel(null);
		}
	}, [items.length]);

	useEffect(() => {
		const currentElement = prefetchSentinel;
		const currentObserver = observer.current;

		if (currentElement) {
			currentObserver.observe(currentElement);
		}

		return () => {
			if (currentElement) {
				currentObserver.unobserve(currentElement);
			}
		};
	}, [prefetchSentinel]);

	useEffect(() => {
		fetchLogs(page, mode, appliedFiltersRef.current);
	}, [page, fetchLogs]);

	function resetAndFetch(filters) {
		hasMore.current = true;
		setAppliedFilters(filters);
		setMode(DATA_MODES.REPLACE);
		setItems([]);
		setError(false);
		setPrefetchSentinel(null);
		if (page === 1) {
			fetchLogs(1, DATA_MODES.REPLACE, filters);
		} else {
			setPage(1);
		}
	}

	function handleApply(filters) {
		resetAndFetch(filters);
	}

	function handleClear() {
		resetAndFetch({});
	}

	function retry() {
		hasMore.current = true;
		setMode(DATA_MODES.REPLACE);
		setError(false);
		if (page === 1) {
			fetchLogs(1, DATA_MODES.REPLACE, appliedFiltersRef.current);
		} else {
			setPage(1);
		}
	}

	function renderTable() {
		return (
			<Table>
				<Trow head top="4.5rem" >
					<Thead>Time</Thead>
					<Thead>Action</Thead>
					<Thead>Actor</Thead>
					<Thead>Target Type</Thead>
					<Thead>Target Id</Thead>
					<Thead>Details</Thead>
				</Trow>
				<Tbody>
					{items.map((log, index, array) => {
						const { _id, createdAt, action, actor, target, details } = log;
						const timeString = createdAt ? formatDisplayDate(createdAt) : "-";
						const actorLabel = actor?.email || actor?.uid || "-";
						const observeRow = array.length >= 3 && index === array.length - 3;
						const hasDetailsToDisplay = isEmpty(details) ? false : true;

						return (
							<Trow
								hoverEffect={false}
								key={_id}
								innerRef={(node) => {
									if (observeRow && node) {
										setPrefetchSentinel((current) => (current === node ? current : node));
									}
								}}>
								<Tcol borderRadius="10px 0 0 10px">{timeString}</Tcol>
								<Tcol>{beautifyKey(action)}</Tcol>
								<Tcol>{actorLabel}</Tcol>
								<Tcol>{target?.type ? beautifyKey(target.type) : "-"}</Tcol>
								<Tcol>{target?.id || "-"}</Tcol>
								<Tcol borderRadius="0 10px 10px 0">
									{hasDetailsToDisplay ? <Button onClick={() => setLogDetails(details)}>Details</Button> : "-"}
								</Tcol>
							</Trow>
						);
					})}
				</Tbody>
			</Table>
		);
	}

	function renderDetailsBody(details) {
		return isEmpty(details) ? <div>No details found</div> : <pre>{JSON.stringify(details, null, 4)}</pre>;
	}

	function closeDetailsModal() {
		setLogDetails(null);
	}

	function renderContent() {
		if (error && !loading && items.length === 0) {
			return <EmptyState retry={retry} />;
		}
		if (loading && mode === DATA_MODES.REPLACE && items.length === 0) {
			return (
				<CenteredContainer>
					<Loader />
				</CenteredContainer>
			);
		}
		if (items.length === 0 && !loading) {
			return <EmptyState retry={retry} />;
		}
		return (
			<>
				{renderTable()}
				{loading && mode === DATA_MODES.APPEND ? (
					<CenteredContainer>
						<Loader />
					</CenteredContainer>
				) : null}
			</>
		);
	}

	return (
		<Page>
			<Header>Audit Logs</Header>
			<AuditLogFilters onApply={handleApply} onClear={handleClear} />
			{renderContent()}
			<Modal
				title="Audit Log Details"
				isOpen={logDetails !== null}
				onClose={closeDetailsModal}>
				{renderDetailsBody(logDetails)}
			</Modal>
		</Page>
	);
};

export default AuditLogs;
